import { THEME_NAME } from "../api/themes";
import { PORTAL_LOGO_SIZES, type PortalLayout, type PortalLogoSize } from "../portal/api";
import { COPY_ENTRIES, copyEntry, copyProblem, LEGACY_COPY_KEYS, normaliseCopyValue } from "../portal/copy";

/**
 * A portal theme as a file an operator downloads, edits and uploads again.
 *
 *   { "format": "ankhos-portal-theme", "version": 2, "name", "ground", "ink",
 *     "accent", "backdrop", "layout", "cardOpacity", "logoSize",
 *     "copy": { key: text, ... },
 *     "logo": { "type", "base64" } | null, "background": { "type", "base64" } | null }
 *
 * Version 1 (no backdrop, layout, cardOpacity or background) is still read: its
 * page settings are today's -- the ground colour, the compact card, no image.
 * A version 2 file without "logoSize" (written before 2026-10-30) is medium.
 *
 * 🔑 THE FILE LISTS EVERY TEXT. `copy` carries all catalogue keys in page order
 * with the text the page will show, so an operator edits wording in place
 * instead of looking keys up. On upload a value equal to its default is not an
 * override. The four legacy heading keys are the exception: they are written
 * empty unless the theme sets them, because their real text lives in the
 * hotspot's own tables and a default here would be a wrong answer.
 *
 * 🔑 UPLOADING SAVES NOTHING. A parsed file only loads the editor; the operator
 * saves, and radhotspotapi validates again. Design: radius-wiki
 * portal-brand-from-logo.md, "Portal text per theme" and "Page background,
 * layout, contact and legal pages".
 */

export const THEME_FILE_FORMAT = "ankhos-portal-theme";
export const THEME_FILE_VERSION = 2;
/** radhotspotapi's decoded logo limit. */
export const MAX_LOGO_BYTES = 150 * 1024;
/** radhotspotapi's decoded background image limit. */
export const MAX_BACKGROUND_BYTES = 600 * 1024;
/** A theme file with a logo and a background is at most ~1.1 MB; anything far larger is not one. */
export const MAX_THEME_FILE_BYTES = 2 * 1024 * 1024;

export const LAYOUTS: readonly PortalLayout[] = ["compact", "wide", "full"];
export const MIN_CARD_OPACITY = 80;

const HEX = /^#[0-9A-Fa-f]{6}$/;
const BASE64 = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

export type LogoType = "image/png" | "image/jpeg";
export interface ThemeLogo {
  type: LogoType;
  base64: string;
}
/** A background image travels exactly as a logo does. */
export type ThemeImage = ThemeLogo;

/**
 * What the editor holds: colours, page settings, the operator's text overrides,
 * and images to upload (null keeps the stored one).
 */
export interface ThemeContent {
  name: string;
  ground: string;
  ink: string;
  accent: string;
  /** Page colour as #RRGGBB, or "" for the ground colour. */
  backdrop: string;
  layout: PortalLayout;
  /** 80-100: the card's opacity over a background image. */
  cardOpacity: number;
  /** How large the portal draws the logo; medium is the size before the choice. */
  logoSize: PortalLogoSize;
  copy: Record<string, string>;
  logo: ThemeLogo | null;
  background: ThemeImage | null;
}

/** Today's page: the ground colour, the compact card, fully opaque, no image, a medium logo. */
export const PAGE_DEFAULTS = { backdrop: "", layout: "compact" as PortalLayout, cardOpacity: 100, logoSize: "medium" as PortalLogoSize, background: null };

export const isHexColour = (v: string) => HEX.test(v);
export const isLayout = (v: unknown): v is PortalLayout => (LAYOUTS as readonly unknown[]).includes(v);
export const isLogoSize = (v: unknown): v is PortalLogoSize => (PORTAL_LOGO_SIZES as readonly unknown[]).includes(v);
export const isCardOpacity = (v: unknown): v is number => Number.isInteger(v) && (v as number) >= MIN_CARD_OPACITY && (v as number) <= 100;
export const themeFileName = (name: string) => `${name || "theme"}.ankhos-theme.json`;

export function bytesToBase64(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

export function base64ToBytes(b64: string): Uint8Array | null {
  if (!BASE64.test(b64)) return null;
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

/** The image type from the bytes themselves, as radhotspotapi sniffs it. Never from a name or a declared type. */
export function sniffLogo(bytes: Uint8Array): LogoType | null {
  const png = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (bytes.length >= 8 && png.every((b, i) => bytes[i] === b)) return "image/png";
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return "image/jpeg";
  return null;
}

function checkImage(b64: string, max: number, what: "logo" | "background image"): { image: ThemeImage } | { problem: string } {
  const bytes = base64ToBytes(b64);
  const The = what === "logo" ? "The logo" : "The background image";
  if (!bytes || bytes.length === 0) return { problem: `${The} is not valid base64.` };
  if (bytes.length > max) return { problem: `${The} is ${bytes.length} bytes; the limit is ${max}.` };
  const type = sniffLogo(bytes);
  if (!type) return { problem: `${The} must be a PNG or JPEG image.` };
  return { image: { type, base64: b64 } };
}

export function checkLogo(b64: string): { logo: ThemeLogo } | { problem: string } {
  const c = checkImage(b64, MAX_LOGO_BYTES, "logo");
  return "problem" in c ? c : { logo: c.image };
}

export function checkBackground(b64: string): { background: ThemeImage } | { problem: string } {
  const c = checkImage(b64, MAX_BACKGROUND_BYTES, "background image");
  return "problem" in c ? c : { background: c.image };
}

/** The operator's text overrides: normalised, empty entries removed. What a save sends. */
export function copyOverrides(copy: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(copy)) {
    const n = normaliseCopyValue(v);
    if (n !== "") out[k] = n;
  }
  return out;
}

/** Key -> why its text cannot be saved, for every set text that breaks a rule. */
export function copyProblems(copy: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(copy)) {
    if (normaliseCopyValue(v) === "") continue;
    const p = copyProblem(k, v);
    if (p) out[k] = p;
  }
  return out;
}

export function toThemeFile(c: ThemeContent): string {
  const overrides = copyOverrides(c.copy);
  const copy: Record<string, string> = {};
  for (const e of COPY_ENTRIES) {
    copy[e.key] = overrides[e.key] ?? (LEGACY_COPY_KEYS.includes(e.key) ? "" : e.default);
  }
  const file = {
    format: THEME_FILE_FORMAT,
    version: THEME_FILE_VERSION,
    name: c.name,
    ground: c.ground.toUpperCase(),
    ink: c.ink.toUpperCase(),
    accent: c.accent.toUpperCase(),
    backdrop: c.backdrop.toUpperCase(),
    layout: c.layout,
    cardOpacity: c.cardOpacity,
    logoSize: c.logoSize,
    copy,
    logo: c.logo,
    background: c.background,
  };
  return JSON.stringify(file, null, 2) + "\n";
}

export type ThemeFileResult =
  /** `version` 1 carries no page settings: the editor keeps the ones it has. */
  | { ok: true; content: ThemeContent; unknown: string[]; version: 1 | 2 }
  | { ok: false; problems: string[] };

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

/**
 * Reads an uploaded theme file. Refused outright: not JSON, not this format or
 * a known version, a colour that is not #RRGGBB, a layout or card opacity out of
 * range, a logo or background that is not a PNG or JPEG within its limit.
 * Loaded with notes: an unknown text key (listed, ignored), a text that breaks a
 * rule (kept, so the editor shows the operator what to fix), a name that is not
 * a valid theme name (the editor asks for one).
 */
export function parseThemeFile(text: string): ThemeFileResult {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { ok: false, problems: ["The file is not valid JSON."] };
  }
  if (!isObject(raw)) return { ok: false, problems: ["The file is not a theme: expected one JSON object."] };

  const problems: string[] = [];
  if (raw.format !== THEME_FILE_FORMAT) problems.push(`This is not an AnkhOS portal theme file ("format" must be "${THEME_FILE_FORMAT}").`);
  if (raw.version !== 1 && raw.version !== THEME_FILE_VERSION) {
    problems.push(`Version ${String(raw.version)} is not supported; this console reads versions 1 and ${THEME_FILE_VERSION}.`);
  }

  const colours: Record<"ground" | "ink" | "accent", string> = { ground: "", ink: "", accent: "" };
  for (const f of ["ground", "ink", "accent"] as const) {
    const v = raw[f];
    if (typeof v !== "string" || !HEX.test(v)) problems.push(`"${f}" must be a colour written as #RRGGBB.`);
    else colours[f] = v.toUpperCase();
  }

  // Version 1 has no page settings: it keeps today's page.
  const page: Pick<ThemeContent, "backdrop" | "layout" | "cardOpacity" | "logoSize" | "background"> = { ...PAGE_DEFAULTS };
  if (raw.version === THEME_FILE_VERSION) {
    if (raw.backdrop !== undefined && raw.backdrop !== "") {
      if (typeof raw.backdrop !== "string" || !HEX.test(raw.backdrop)) problems.push('"backdrop" must be empty or a colour written as #RRGGBB.');
      else page.backdrop = raw.backdrop.toUpperCase();
    }
    if (raw.layout !== undefined) {
      if (!isLayout(raw.layout)) problems.push(`"layout" must be one of ${LAYOUTS.join(", ")}.`);
      else page.layout = raw.layout;
    }
    if (raw.cardOpacity !== undefined) {
      if (!isCardOpacity(raw.cardOpacity)) problems.push(`"cardOpacity" must be a whole number from ${MIN_CARD_OPACITY} to 100.`);
      else page.cardOpacity = raw.cardOpacity;
    }
    if (raw.logoSize !== undefined) {
      if (!isLogoSize(raw.logoSize)) problems.push(`"logoSize" must be one of ${PORTAL_LOGO_SIZES.join(", ")}.`);
      else page.logoSize = raw.logoSize;
    }
    if (raw.background !== null && raw.background !== undefined) {
      if (!isObject(raw.background) || typeof raw.background.base64 !== "string") {
        problems.push('"background" must be null or an object with "base64".');
      } else {
        const c = checkBackground(raw.background.base64);
        if ("problem" in c) problems.push(c.problem);
        else page.background = c.background;
      }
    }
  }

  let logo: ThemeLogo | null = null;
  if (raw.logo !== null && raw.logo !== undefined) {
    if (!isObject(raw.logo) || typeof raw.logo.base64 !== "string") {
      problems.push('"logo" must be null or an object with "base64".');
    } else {
      const c = checkLogo(raw.logo.base64);
      if ("problem" in c) problems.push(c.problem);
      else logo = c.logo;
    }
  }

  const copy: Record<string, string> = {};
  const unknown: string[] = [];
  if (raw.copy !== undefined) {
    if (!isObject(raw.copy)) {
      problems.push('"copy" must be an object of texts.');
    } else {
      for (const [k, v] of Object.entries(raw.copy)) {
        const e = copyEntry(k);
        if (!e) {
          unknown.push(k);
          continue;
        }
        if (typeof v !== "string") {
          problems.push(`copy "${k}" must be text.`);
          continue;
        }
        const n = normaliseCopyValue(v);
        if (n === "") continue;
        if (!LEGACY_COPY_KEYS.includes(k) && n === normaliseCopyValue(e.default)) continue;
        copy[k] = v;
      }
    }
  }

  if (problems.length) return { ok: false, problems };
  const name = typeof raw.name === "string" && THEME_NAME.test(raw.name.trim()) ? raw.name.trim() : "";
  return { ok: true, content: { name, ...colours, ...page, copy, logo }, unknown, version: raw.version === 1 ? 1 : 2 };
}
